/**
 * ========================================
 * DEMO REQUEST SERVICE
 * ========================================
 * API calls for the "Book a Live Demo" module.
 * Endpoints: /api/v1/demo-requests/*
 * `create()` is called from the public landing page before login — apiClient
 * simply omits the Authorization header when no token is present, so no
 * special handling is needed here.
 */

import { apiClient } from './client';
import { handleAPIError } from './error-handler';
import type {
  DemoRequest,
  DemoRequestPublic,
  DemoRequestFilters,
  DemoRequestCreateRequest,
  DemoRequestUpdateRequest,
  AssignTeacherRequest,
  StatusUpdateRequest,
  ScheduleRequest,
  FollowUpRequest,
  FinalResultRequest,
  DemoRequestAuditLogEntry,
  DemoRequestSummary,
  TeacherSummary,
} from '@/types/demoRequest';

interface BackendEnvelope<T> {
  status: string;
  status_code?: number;
  message: string;
  data: T;
  summary?: { total: number; page: number; page_size: number };
}

export interface DemoRequestListResult {
  items: DemoRequest[];
  total: number;
  page: number;
  page_size: number;
}

class DemoRequestService {
  private baseUrl = '/api/v1/demo-requests';

  async create(payload: DemoRequestCreateRequest): Promise<{ data: DemoRequestPublic; message: string }> {
    try {
      const response = await apiClient.post<BackendEnvelope<DemoRequestPublic>>(this.baseUrl, payload);
      return { data: response.data.data, message: response.data.message };
    } catch (error) {
      handleAPIError(error);
    }
  }

  async list(filters?: DemoRequestFilters & { page?: number; page_size?: number }): Promise<DemoRequestListResult> {
    try {
      const response = await apiClient.get<BackendEnvelope<DemoRequest[]>>(this.baseUrl, { params: filters || {} });
      const summary = response.data.summary;
      return {
        items: response.data.data,
        total: summary?.total ?? response.data.data.length,
        page: summary?.page ?? 1,
        page_size: summary?.page_size ?? response.data.data.length,
      };
    } catch (error) {
      handleAPIError(error);
    }
  }

  async getById(id: string): Promise<DemoRequest> {
    try {
      const response = await apiClient.get<BackendEnvelope<DemoRequest>>(`${this.baseUrl}/${id}`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async getSummary(): Promise<DemoRequestSummary> {
    try {
      const response = await apiClient.get<BackendEnvelope<DemoRequestSummary>>(`${this.baseUrl}/summary`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async getTeachers(): Promise<TeacherSummary[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<TeacherSummary[]>>(`${this.baseUrl}/teachers`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async getHistory(id: string): Promise<DemoRequestAuditLogEntry[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<DemoRequestAuditLogEntry[]>>(`${this.baseUrl}/${id}/history`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async update(id: string, payload: DemoRequestUpdateRequest): Promise<DemoRequest> {
    try {
      const response = await apiClient.put<BackendEnvelope<DemoRequest>>(`${this.baseUrl}/${id}`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async assignTeacher(id: string, payload: AssignTeacherRequest): Promise<DemoRequest> {
    try {
      const response = await apiClient.post<BackendEnvelope<DemoRequest>>(`${this.baseUrl}/${id}/assign-teacher`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async changeStatus(id: string, payload: StatusUpdateRequest): Promise<DemoRequest> {
    try {
      const response = await apiClient.post<BackendEnvelope<DemoRequest>>(`${this.baseUrl}/${id}/status`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async schedule(id: string, payload: ScheduleRequest): Promise<DemoRequest> {
    try {
      const response = await apiClient.post<BackendEnvelope<DemoRequest>>(`${this.baseUrl}/${id}/schedule`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async followUp(id: string, payload: FollowUpRequest): Promise<DemoRequest> {
    try {
      const response = await apiClient.post<BackendEnvelope<DemoRequest>>(`${this.baseUrl}/${id}/follow-up`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async setFinalResult(id: string, payload: FinalResultRequest): Promise<DemoRequest> {
    try {
      const response = await apiClient.post<BackendEnvelope<DemoRequest>>(`${this.baseUrl}/${id}/final-result`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }
}

export const demoRequestService = new DemoRequestService();
export default demoRequestService;
